import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";
import { config } from "../../utils/apiUrl";

const api =
  new API({
    tokenKey:
      "platformAccessToken",
  });

const initialState = {
  leads: [],
  selectedLead: null,
  summary: {
    total: 0,
    NEW: 0,
    CONTACTED: 0,
    QUALIFIED: 0,
    CONVERTED: 0,
    CLOSED: 0,
  },
  filters: {
    page: 1,
    limit: 20,
    search: "",
    status: "",
    tenantId: "",
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  filterOptions: {
    statuses: [],
    tenants: [],
  },
  listLoading: false,
  detailsLoading: false,
  actionLoading: false,
  error: null,
};

const qs = (params = {}) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      sp.append(key, value);
    }
  });
  return sp.toString();
};

const err = (error) => ({
  message:
    error?.response?.data?.message ||
    error?.response?.message ||
    error?.message ||
    "Something went wrong.",
});

export const fetchPlatformBusinessPartnerLeads =
  createAsyncThunk(
    "platformBusinessPartnerLead/list",
    async (params = {}, { rejectWithValue }) => {
      try {
        const query = qs(params);
        return await api.get(
          query
            ? `${config.platformBusinessPartnerLeads}?${query}`
            : config.platformBusinessPartnerLeads,
          true
        );
      } catch (error) {
        return rejectWithValue(err(error));
      }
    }
  );

export const fetchPlatformBusinessPartnerLeadSummary =
  createAsyncThunk(
    "platformBusinessPartnerLead/summary",
    async (_, { rejectWithValue }) => {
      try {
        return await api.get(
          config.platformBusinessPartnerLeadSummary,
          true
        );
      } catch (error) {
        return rejectWithValue(err(error));
      }
    }
  );

export const fetchPlatformBusinessPartnerLeadFilters =
  createAsyncThunk(
    "platformBusinessPartnerLead/filters",
    async (_, { rejectWithValue }) => {
      try {
        return await api.get(
          config.platformBusinessPartnerLeadFilters,
          true
        );
      } catch (error) {
        return rejectWithValue(err(error));
      }
    }
  );

export const fetchPlatformBusinessPartnerLeadById =
  createAsyncThunk(
    "platformBusinessPartnerLead/details",
    async (id, { rejectWithValue }) => {
      try {
        return await api.get(
          config.platformBusinessPartnerLeadById(id),
          true
        );
      } catch (error) {
        return rejectWithValue(err(error));
      }
    }
  );

export const updatePlatformBusinessPartnerLeadStatus =
  createAsyncThunk(
    "platformBusinessPartnerLead/status",
    async ({ id, status }, { rejectWithValue }) => {
      try {
        return await api.patch(
          config.platformBusinessPartnerLeadStatus(id),
          { status },
          true
        );
      } catch (error) {
        return rejectWithValue(err(error));
      }
    }
  );

export const updatePlatformBusinessPartnerLeadNotes =
  createAsyncThunk(
    "platformBusinessPartnerLead/notes",
    async ({ id, notes }, { rejectWithValue }) => {
      try {
        return await api.patch(
          config.platformBusinessPartnerLeadNotes(id),
          { notes },
          true
        );
      } catch (error) {
        return rejectWithValue(err(error));
      }
    }
  );

export const convertPlatformBusinessPartnerLead =
  createAsyncThunk(
    "platformBusinessPartnerLead/convert",
    async (
      {
        id,
        convertedTenantId,
        conversionNotes,
      },
      { rejectWithValue }
    ) => {
      try {
        return await api.post(
          config.platformBusinessPartnerLeadConvert(id),
          {
            convertedTenantId,
            conversionNotes,
          },
          true
        );
      } catch (error) {
        return rejectWithValue(err(error));
      }
    }
  );

const slice = createSlice({
  name: "platformBusinessPartnerLead",
  initialState,
  reducers: {
    setPlatformBusinessPartnerLeadFilters(state, action) {
      state.filters = {
        ...state.filters,
        ...(action.payload || {}),
      };
    },
    clearSelectedPlatformBusinessPartnerLead(state) {
      state.selectedLead = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlatformBusinessPartnerLeads.pending, (state) => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(fetchPlatformBusinessPartnerLeads.fulfilled, (state, action) => {
        state.listLoading = false;
        state.leads = Array.isArray(action.payload?.data)
          ? action.payload.data
          : [];
        state.pagination = {
          ...state.pagination,
          ...(action.payload?.pagination || {}),
        };
      })
      .addCase(fetchPlatformBusinessPartnerLeads.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchPlatformBusinessPartnerLeadSummary.fulfilled, (state, action) => {
        state.summary = {
          ...state.summary,
          ...(action.payload?.data || {}),
        };
      });

    builder
      .addCase(fetchPlatformBusinessPartnerLeadFilters.fulfilled, (state, action) => {
        state.filterOptions = {
          statuses: Array.isArray(action.payload?.data?.statuses)
            ? action.payload.data.statuses
            : [],
          tenants: Array.isArray(action.payload?.data?.tenants)
            ? action.payload.data.tenants
            : [],
        };
      });

    builder
      .addCase(fetchPlatformBusinessPartnerLeadById.pending, (state) => {
        state.detailsLoading = true;
      })
      .addCase(fetchPlatformBusinessPartnerLeadById.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.selectedLead = action.payload?.data || null;
      });

    [
      updatePlatformBusinessPartnerLeadStatus,
      updatePlatformBusinessPartnerLeadNotes,
      convertPlatformBusinessPartnerLead,
    ].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.actionLoading = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.actionLoading = false;
          const updated = action.payload?.data;
          if (!updated) return;

          const id = String(updated._id || updated.id || "");
          state.leads = state.leads.map((lead) =>
            String(lead._id || lead.id || "") === id
              ? updated
              : lead
          );

          if (
            String(state.selectedLead?._id || state.selectedLead?.id || "") === id
          ) {
            state.selectedLead = updated;
          }
        })
        .addCase(thunk.rejected, (state, action) => {
          state.actionLoading = false;
          state.error = action.payload;
        });
    });
  },
});

export const {
  setPlatformBusinessPartnerLeadFilters,
  clearSelectedPlatformBusinessPartnerLead,
} = slice.actions;

export const selectPlatformBusinessPartnerLeadState =
  (state) =>
    state.platformBusinessPartnerLead ||
    initialState;

export default slice.reducer;
